import React, { useEffect, useState } from 'react';
import Sidebar from '../Components/Sidebar';
import MobileNav from '../Components/MobileNav';
import { useIsMobile } from '../hooks/useMediaQuery';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import Loader from '../Components/Loader';

export default function Home() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  
  // Social stats - ADDED
  const [followingCount, setFollowingCount] = useState(0);
  const [followersCount, setFollowersCount] = useState(0);
  
  const [stats, setStats] = useState({
    kills: 0,
    deaths: 0,
    suicides: 0,
    betrayals: 0
  });
  
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [totalClans, setTotalClans] = useState(0);

  useEffect(() => {
    const checkAuth = async () => {
      // Try to get session immediately
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        setUser(session.user);
        loadData(session.user);
        return;
      }
      
      // If no session, wait a bit (OAuth redirect case) then retry
      setTimeout(async () => {
        const { data: { session: retrySession } } = await supabase.auth.getSession();
        if (retrySession?.user) {
          setUser(retrySession.user);
          loadData(retrySession.user);
        } else {
          navigate('/login');
        }
      }, 1000);
    };
    
    checkAuth();
  }, []);

  const loadData = async (authUser) => {
    if (!authUser) {
      setLoading(false);
      return;
    }

    const { data: profileData } = await supabase
      .from('igd')
      .select('*')
      .eq('user_id', authUser.id)
      .maybeSingle();

    if (profileData) {
      setProfile(profileData);
      setStats({
        kills: profileData.kills || 0,
        deaths: profileData.deaths || 0,
        suicides: profileData.suicides || 0,
        betrayals: profileData.betrayals || 0
      });
    }

    // Load social counts - ADDED
    const { count: following } = await supabase
      .from('followers')
      .select('*', { count: 'exact', head: true })
      .eq('follower_id', authUser.id);

    const { count: followers } = await supabase
      .from('followers')
      .select('*', { count: 'exact', head: true })
      .eq('following_id', authUser.id);

    setFollowingCount(following || 0);
    setFollowersCount(followers || 0);

    const { count: playersCount } = await supabase
      .from('igd')
      .select('*', { count: 'exact', head: true });

    const { count: clansCount } = await supabase
      .from('clans')
      .select('*', { count: 'exact', head: true });

    setTotalPlayers(playersCount || 0);
    setTotalClans(clansCount || 0);
    setLoading(false);
  };

  const kdRatio = stats.deaths === 0 ? stats.kills : (stats.kills / stats.deaths).toFixed(2);
  const kdPercent = Math.min((stats.kills / (stats.kills + stats.deaths || 1)) * 100, 100);

  const hasProfile = profile?.avatarName && profile?.avatarName.trim() !== '';

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        background: 'var(--bg)', 
        minHeight: '100vh',
        flexDirection: isMobile ? 'column' : 'row',
      }}>
        {!isMobile && <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />}
        <div style={{ 
          flex: 1, 
          marginLeft: isMobile ? '0' : (collapsed ? '80px' : '280px'), 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: isMobile ? '20px' : '0',
        }}>
          <Loader size={isMobile ? 80 : 120} />
        </div>
        {isMobile && <MobileNav />}
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      background: 'var(--bg)', 
      minHeight: '100vh',
      color: 'var(--text)',
      flexDirection: isMobile ? 'column' : 'row',
    }}>
      {!isMobile && <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />}
      
      <div style={{ 
        flex: 1, 
        marginLeft: isMobile ? '0' : (collapsed ? '80px' : '280px'),
        padding: isMobile ? '16px' : '40px',
        paddingBottom: isMobile ? '90px' : '40px',
        transition: 'margin-left 0.3s ease',
        overflowX: 'hidden',
      }}>
        {/* Hero Card */}
        <div style={{
          background: 'linear-gradient(135deg, var(--card-bg) 0%, rgba(56, 189, 248, 0.1) 100%)',
          borderRadius: isMobile ? '16px' : '24px',
          border: '1px solid var(--border)',
          padding: isMobile ? '20px' : '40px',
          marginBottom: isMobile ? '16px' : '24px',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: isMobile ? '200px' : '400px',
            height: isMobile ? '200px' : '400px',
            background: 'radial-gradient(circle, rgba(56, 189, 248, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
          }} />
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? '16px' : '30px', 
            flexWrap: 'wrap', 
            position: 'relative', 
            zIndex: 1,
          }}>
            {/* Avatar */}
            <div style={{ position: 'relative' }}>
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt="avatar"
                  style={{
                    width: isMobile ? '80px' : '120px',
                    height: isMobile ? '80px' : '120px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: isMobile ? '3px solid #38bdf8' : '4px solid #38bdf8',
                    boxShadow: '0 0 40px rgba(56, 189, 248, 0.4)',
                  }}
                />
              ) : (
                <div style={{
                  width: isMobile ? '80px' : '120px',
                  height: isMobile ? '80px' : '120px',
                  borderRadius: '50%',
                  background: 'var(--bg)',
                  border: isMobile ? '3px dashed #475569' : '4px dashed #475569',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '32px' : '48px',
                  color: '#475569',
                }}>
                  ?
                </div>
              )}
            </div>

            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ 
                fontSize: isMobile ? '24px' : '36px', 
                fontWeight: 'bold',
                marginBottom: isMobile ? '8px' : '10px',
                background: hasProfile ? 'linear-gradient(135deg, #38bdf8, #818cf8)' : 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: hasProfile ? 'transparent' : '#475569',
                color: hasProfile ? undefined : '#475569',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {profile?.avatarName || 'Unknown Soldier'}
              </h1>
              
              <div style={{ 
                display: 'flex', 
                gap: isMobile ? '8px' : '12px', 
                flexWrap: 'wrap', 
                marginBottom: isMobile ? '10px' : '15px', 
                minHeight: isMobile ? '32px' : '36px',
              }}>
                {profile?.rank && (
                  <span style={{
                    padding: isMobile ? '6px 12px' : '8px 16px',
                    background: 'rgba(56, 189, 248, 0.15)',
                    color: '#38bdf8',
                    borderRadius: '9999px',
                    fontSize: isMobile ? '11px' : '13px',
                    fontWeight: 'bold',
                    border: '1px solid rgba(56, 189, 248, 0.3)',
                  }}>
                    ⭐ {profile.rank}
                  </span>
                )}
                {profile?.clan && (
                  <span style={{
                    padding: isMobile ? '6px 12px' : '8px 16px',
                    background: 'rgba(139, 92, 246, 0.15)',
                    color: '#8b5cf6',
                    borderRadius: '9999px',
                    fontSize: isMobile ? '11px' : '13px',
                    fontWeight: 'bold',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                  }}>
                    🛡️ {profile.clan}
                  </span>
                )}
                {profile?.token && (
                  <span style={{
                    padding: isMobile ? '6px 12px' : '8px 16px',
                    background: 'rgba(251, 191, 36, 0.15)',
                    color: '#fbbf24',
                    borderRadius: '9999px',
                    fontSize: isMobile ? '11px' : '13px',
                    fontWeight: 'bold',
                    border: '1px solid rgba(251, 191, 36, 0.3)',
                  }}>
                    🪙 {profile.token}
                  </span>
                )}
              </div>

              <p style={{ 
                color: '#64748b', 
                fontSize: isMobile ? '14px' : '16px', 
                minHeight: '24px',
              }}>
                {profile?.current_award ? '🏆 Alpha Medal of Honour' : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Social Stats - ADDED SECTION */}
        {user && (
          <div style={{ marginBottom: isMobile ? '16px' : '24px' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: isMobile ? '12px' : '16px',
            }}>
              {/* Following Card */}
              <div 
                onClick={() => navigate('/following')}
                style={{
                  background: 'var(--card-bg)',
                  padding: isMobile ? '16px' : '20px',
                  borderRadius: isMobile ? '12px' : '16px',
                  border: '1px solid var(--border)',
                  cursor: 'pointer',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'transform 0.2s, border-color 0.2s',
                }}
                onMouseEnter={e => !isMobile && (e.currentTarget.style.transform = 'translateY(-2px)')}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                onTouchStart={e => e.currentTarget.style.transform = 'scale(0.98)'}
                onTouchEnd={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                <div style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '-10px',
                  width: '60px',
                  height: '60px',
                  background: 'rgba(56, 189, 248, 0.1)',
                  borderRadius: '50%',
                  filter: 'blur(20px)',
                }} />
                <div style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: '4px' }}>👥</div>
                <div style={{ 
                  fontSize: isMobile ? '20px' : '24px', 
                  fontWeight: 'bold', 
                  color: '#38bdf8',
                  marginBottom: '2px',
                }}>
                  {followingCount.toLocaleString()}
                </div>
                <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Following</div>
              </div>

              {/* Followers Card */}
              <div 
                onClick={() => navigate('/followers')}
                style={{
                  background: 'var(--card-bg)',
                  padding: isMobile ? '16px' : '20px',
                  borderRadius: isMobile ? '12px' : '16px',
                  border: '1px solid var(--border)',
                  cursor: 'pointer',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'transform 0.2s, border-color 0.2s',
                }}
                onMouseEnter={e => !isMobile && (e.currentTarget.style.transform = 'translateY(-2px)')}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                onTouchStart={e => e.currentTarget.style.transform = 'scale(0.98)'}
                onTouchEnd={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                <div style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '-10px',
                  width: '60px',
                  height: '60px',
                  background: 'rgba(34, 197, 94, 0.1)',
                  borderRadius: '50%',
                  filter: 'blur(20px)',
                }} />
                <div style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: '4px' }}>👤</div>
                <div style={{ 
                  fontSize: isMobile ? '20px' : '24px', 
                  fontWeight: 'bold', 
                  color: '#22c55e',
                  marginBottom: '2px',
                }}>
                  {followersCount.toLocaleString()}
                </div>
                <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Followers</div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Overview */}
        <div style={{ marginBottom: isMobile ? '16px' : '24px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: isMobile ? '16px' : '20px',
            flexWrap: 'wrap',
            gap: '10px',
          }}>
            <h2 style={{ 
              fontSize: isMobile ? '20px' : '24px', 
              fontWeight: 'bold',
            }}>
              Combat Performance
            </h2>
            {hasProfile && (
              <div style={{
                padding: isMobile ? '6px 12px' : '8px 16px',
                background: kdRatio >= 1 ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                color: kdRatio >= 1 ? '#22c55e' : '#ef4444',
                borderRadius: '9999px',
                fontSize: isMobile ? '12px' : '14px',
                fontWeight: 'bold',
              }}>
                K/D: {kdRatio}
              </div>
            )}
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: isMobile ? '12px' : '20px',
          }}>
            <StatCard 
              icon="⚔️"
              label="Kills"
              value={stats.kills}
              color="#22c55e"
              gradient="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
              empty={!hasProfile}
              isMobile={isMobile}
            />
            
            <StatCard 
              icon="💀"
              label="Deaths"
              value={stats.deaths}
              color="#ef4444"
              gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
              empty={!hasProfile}
              isMobile={isMobile}
            />
            
            <StatCard 
              icon="💔"
              label="Suicides"
              value={stats.suicides}
              color="#8b5cf6"
              gradient="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
              empty={!hasProfile}
              isMobile={isMobile}
            />
            
            <StatCard 
              icon="🔪"
              label="Betrayals"
              value={stats.betrayals}
              color="#f97316"
              gradient="linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
              empty={!hasProfile}
              isMobile={isMobile}
            />
          </div>
        </div>

        {/* K/D Progress */}
        {hasProfile && (
          <div style={{
            background: 'var(--card-bg)',
            padding: isMobile ? '20px' : '30px',
            borderRadius: isMobile ? '16px' : '20px',
            border: '1px solid var(--border)',
            marginBottom: isMobile ? '16px' : '24px',
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginBottom: '15px',
            }}>
              <span style={{ color: '#64748b', fontSize: isMobile ? '13px' : '14px' }}>
                Combat Efficiency
              </span>
              <span style={{ 
                color: kdRatio >= 1 ? '#22c55e' : '#ef4444', 
                fontWeight: 'bold',
                fontSize: isMobile ? '13px' : '14px',
              }}>
                {kdPercent.toFixed(1)}%
              </span>
            </div>
            <div style={{
              height: isMobile ? '10px' : '12px',
              background: 'var(--bg)',
              borderRadius: '9999px',
              overflow: 'hidden',
            }}>
              <div style={{
                width: `${kdPercent}%`,
                height: '100%',
                background: kdRatio >= 1 
                  ? 'linear-gradient(90deg, #22c55e, #16a34a)' 
                  : 'linear-gradient(90deg, #ef4444, #dc2626)',
                borderRadius: '9999px',
                transition: 'width 1s ease',
              }} />
            </div>
            <p style={{ 
              color: '#64748b', 
              fontSize: isMobile ? '12px' : '13px', 
              marginTop: '10px',
              lineHeight: '1.5',
            }}>
              {kdRatio >= 1 
                ? '🔥 Excellent performance! You dominate the battlefield.' 
                : '💪 Keep fighting! Your K/D will improve with practice.'}
            </p>
          </div>
        )}

        {/* Community Stats */}
        <div style={{ marginBottom: isMobile ? '20px' : '30px' }}>
          <h2 style={{ 
            fontSize: isMobile ? '20px' : '24px', 
            fontWeight: 'bold', 
            marginBottom: isMobile ? '16px' : '20px',
          }}>
            Community
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: isMobile ? '12px' : '20px',
          }}>
            <CommunityCard 
              icon="🎮"
              value={totalPlayers}
              label="Active Soldiers"
              color="#38bdf8"
              isMobile={isMobile}
            />
            <CommunityCard 
              icon="🛡️"
              value={totalClans}
              label="Total Clans" 
              color="#8b5cf6"
              isMobile={isMobile}
            />
          </div>
        </div>
      </div>
      
      {isMobile && <MobileNav />}
    </div>
  );
}

// Stat Card Component
function StatCard({ icon, label, value, color, gradient, empty, isMobile }) {
  return (
    <div style={{
      background: 'var(--card-bg)',
      padding: isMobile ? '16px' : '25px',
      borderRadius: isMobile ? '12px' : '16px',
      border: '1px solid var(--border)',
      position: 'relative',
      overflow: 'hidden',
      transition: 'transform 0.3s',
      opacity: empty ? 0.6 : 1,
    }}
    onMouseEnter={e => !isMobile && !empty && (e.currentTarget.style.transform = 'translateY(-5px)')}
    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
    >
      {!empty && (
        <div style={{
          position: 'absolute',
          top: '-20px',
          right: '-20px',
          width: '100px',
          height: '100px',
          background: gradient,
          opacity: '0.1',
          borderRadius: '50%',
          filter: 'blur(40px)',
        }} />
      )}
      
      <div style={{
        width: isMobile ? '40px' : '50px',
        height: isMobile ? '40px' : '50px',
        borderRadius: '12px',
        background: empty ? 'var(--bg)' : gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: isMobile ? '20px' : '24px',
        marginBottom: isMobile ? '10px' : '15px',
        border: empty ? '2px dashed var(--border)' : 'none',
        color: empty ? '#475569' : 'inherit',
      }}>
        {empty ? '—' : icon}
      </div>
      
      <div style={{ 
        fontSize: isMobile ? '24px' : '32px', 
        fontWeight: 'bold', 
        color: empty ? '#475569' : color, 
        marginBottom: '5px',
      }}>
        {empty ? '—' : value.toLocaleString()}
      </div>
      <div style={{ 
        fontSize: isMobile ? '11px' : '13px', 
        color: '#64748b', 
        textTransform: 'uppercase', 
        letterSpacing: '1px',
      }}>
        {label}
      </div>
    </div>
  );
}

// Community Card Component
function CommunityCard({ icon, value, label, color, isMobile }) {
  return (
    <div style={{
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      padding: isMobile ? '20px' : '30px',
      borderRadius: isMobile ? '12px' : '16px',
      border: '1px solid var(--border)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute',
        top: '-20px',
        right: '-20px',
        fontSize: isMobile ? '60px' : '100px',
        opacity: '0.05',
        color: color,
      }}>
        {icon}
      </div>
      
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ 
          fontSize: isMobile ? '28px' : '40px', 
          fontWeight: 'bold', 
          color: color, 
          marginBottom: '5px',
        }}>
          {value.toLocaleString()}
        </div>
        <div style={{ 
          color: '#64748b', 
          fontSize: isMobile ? '14px' : '15px',
        }}>
          {label}
        </div>
      </div>
    </div>
  );
}